import { gql, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import { useGroupFilter } from 'pared/Routes/renderer/groupFilter'

import useDate, {
  IDataType as IDateDataType,
  IOptionType as IDateOptionType,
} from '../common/date'

export interface IOptionType {
  dateOptions?: IDateOptionType
}

export interface IDataType extends IDateDataType {
  bbbTrendPurchaseCategories?: {
    value: string[][]
    onChange: (value: string[][]) => void
    options: {
      id: string
      parentId: string
      displayName: string
    }[]
  }
  bbbPurchaseCategories?: {
    loading: boolean
    disable: boolean
    value: string[][]
    onChange: (value: string[][]) => void
    options: {
      id: string
      parentId: string
      displayName: string
    }[]
  }
}

interface IPurchaseSummaryType {
  trendLocationPurchase: {
    nodes: {
      itemDetails: Record<
        string,
        {
          type: 'all' | 'item' | 'category'
          item: string
        }
      >
    }[]
  }
  purchase: {
    nodes: {
      reportResult: {
        tableData: {
          category?: string
        }[]
      }
    }[]
  }
}

const query = gql`
  query BbbLocationItems(
    $iLocationId: Int!
    $iEndDate: Date!
    $iGroupBy: String!
    $iInputParams: JSON!
  ) {
    trendLocationPurchase(
      iLocationId: $iLocationId
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
    ) {
      nodes {
        itemDetails
      }
    }

    purchase: getCustomizedReport(
      iCustomizedReportName: "LOCATION_PURCHASE_TABLE_V2"
      iInputParams: $iInputParams
    ) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }
  }
`

const ALL_CATEGORIES = 'All Categories'

const useBbbLocationItems = ({ dateOptions = {} }: IOptionType) => {
  const { groupFilter } = useGroupFilter()
  const locationId = groupFilter?.ids?.[0] || 0

  const { date } = useDate(dateOptions)
  const startDate = date?.value.dateRange.startDateStr
  const endDate = date?.value.dateRange.endDateStr
  const dateType = date?.value.type
  const iGroupBy = ((t) => {
    switch (t) {
      case 'year':
        return 'business_quarter'

      case 'quarter':
        return 'business_month'

      default:
        return 'business_week'
    }
  })(dateType)

  const trendPurchaseItemsHook = useState<string[][]>([])
  const purchaseItemsHook = useState<string[][]>([])

  const { data, loading } = useQuery<IPurchaseSummaryType>(query, {
    variables: {
      iLocationId: locationId,
      iEndDate: endDate,
      iGroupBy,
      iInputParams: {
        locationId,
        startDate,
        endDate,
      },
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    date,
    bbbTrendPurchaseCategories:
      useMemo((): IDataType['bbbTrendPurchaseCategories'] => {
        const tableData = data?.trendLocationPurchase.nodes.reduce(
          (result, n) => ({
            ...result,
            ...n.itemDetails,
          }),
          {} as NonNullable<IPurchaseSummaryType>['trendLocationPurchase']['nodes'][number]['itemDetails'],
        )

        return {
          value: trendPurchaseItemsHook[0],
          onChange: trendPurchaseItemsHook[1],
          options: (() => {
            if (!tableData) return []

            if (Object.keys(tableData).length === 0)
              return [
                {
                  id: 'All Items',
                  parentId: 'root' as const,
                  displayName: 'All Items',
                },
              ]

            return [
              {
                id: 'category',
                parentId: 'root' as const,
                displayName: 'Category',
              },
              {
                id: 'item',
                parentId: 'root' as const,
                displayName: 'Item',
              },
              ...Object.values(tableData).map((t) => ({
                id: t.item,
                parentId: t.type === 'all' ? 'root' : t.type,
                displayName: t.item,
              })),
            ].sort((a, b) => a.displayName.localeCompare(b.displayName))
          })(),
        }
      }, [trendPurchaseItemsHook, data]),

    bbbPurchaseCategories: useMemo((): IDataType['bbbPurchaseCategories'] => {
      const tableData = data?.purchase.nodes[0]?.reportResult.tableData

      return {
        loading,
        disable: !tableData && !loading,
        value: purchaseItemsHook[0],
        onChange: purchaseItemsHook[1],
        options: !tableData
          ? []
          : [
              {
                id: ALL_CATEGORIES,
                parentId: 'root' as const,
                displayName: ALL_CATEGORIES,
              },
              ...tableData
                .reduce(
                  (result, data) =>
                    !data.category || result.includes(data.category)
                      ? result
                      : [...result, data.category],
                  [] as string[],
                )
                .sort()
                .map((category) => ({
                  id: category,
                  parentId: 'root' as const,
                  displayName: category,
                })),
            ],
      }
    }, [purchaseItemsHook, data, loading]),
  }
}

export default useBbbLocationItems
